import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from './Toast';
import { useDealPowers } from '../data/permissions';
import { brainInventory, brainReindex } from '../data/legalApi';
import '../pages/Brain.css';

// Project knowledge — what this project's brain holds and what it is missing.
// Reads /legal/brain/inventory (the corpus gathered exactly as a build would,
// without embedding) and shows: freshness against the live index, chunks by
// source and by document, deal-point coverage, and a gap list with the fix
// for each. "Rebuild" goes through the existing reindex endpoint.

const SOURCE_LABEL = { deal: 'project overview', ecosystem: 'agreement ecosystem', clause: 'clauses (working drafts)', artifact: 'clauses (artifacts)', profile: 'import profiles', analysis: 'version analyses', correspondence: 'correspondence', revision: 'revision history', dealPoint: 'deal points' };
const SOURCE_HELP = {
  deal: 'Name, type, parties, tags, and the structure note.',
  ecosystem: 'Which document owns which layer (HoldCo vs OpCo, etc.).',
  clause: 'Every clause of each ready working draft\u2019s current version.',
  artifact: 'Clauses of exports and reference copies.',
  profile: 'Smart-import summaries: type, governing law, one-paragraph gist.',
  analysis: 'AI change analyses and key asks per received version.',
  correspondence: 'Emails and notes filed with received drafts.',
  revision: 'Each version\u2019s instruction and per-change rationale.',
  dealPoint: 'The register: status, positions, linked clauses, AI suggestions.',
};
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const sev = (s) => ({ danger: 'b-danger', warn: 'b-warn', info: 'b-info' }[s] || 'b-muted');
const docStatus = (s) => ({ ready: 'b-ok', queued: 'b-info', processing: 'b-info', error: 'b-danger' }[s] || 'b-muted');

export default function KnowledgeTab({ scope }) {
  const { can } = useDealPowers(scope.deal);
  const toast = useToast();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr('');
    try { setInv(await brainInventory(scope.org, scope.deal)); } catch (e) { setErr(e.message); }
  };
  useEffect(() => { setInv(null); load(); }, [scope.org, scope.deal]); // eslint-disable-line react-hooks/exhaustive-deps

  const rebuild = async () => {
    setBusy(true);
    try {
      const r = await brainReindex({ orgId: scope.org, dealId: scope.deal });
      toast(r.rebuilt ? `Brain rebuilt: ${r.chunkCount} chunks indexed` : r.building ? 'Rebuild handed to the brain worker; check back in a minute' : 'Brain already current', 'ok');
      await load();
    } catch (e) {
      toast(e.message, 'danger');
    } finally { setBusy(false); }
  };

  if (err) return <div className="cs-banner b-danger">{err}</div>;
  if (!inv) return <div className="cs-empty">Taking inventory of the project brain…</div>;

  const { brain, bySource, docs, points, gaps, chunkCount, deal } = inv;
  const warn = gaps.filter((g) => g.severity !== 'info');
  const info = gaps.filter((g) => g.severity === 'info');
  const sourceRows = Object.entries(bySource).sort((a, b) => b[1] - a[1]);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
      <div className="u-split" style={{ alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ margin: 0 }}>Project knowledge</h3>
          <p className="u-small u-muted" style={{ margin: 'var(--sp-1) 0 0' }}>
            Everything the project brain can cite when you ask it a question. Content the brain cannot see is listed under gaps.
          </p>
        </div>
        {can('agent') && (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy || brain.fresh} onClick={rebuild} title={brain.fresh ? 'The index already matches the content' : 'Re-index now'}>
            {busy ? 'Rebuilding…' : brain.fresh ? 'Index current' : 'Rebuild index'}
          </button>
        )}
      </div>

      <div className="brain-strip">
        <div className="cs-stat is-compact"><span className="cs-stat-label">Index</span><span className="cs-stat-value" style={{ color: brain.fresh ? 'var(--ok)' : brain.builtAt ? 'var(--warn)' : 'var(--muted)' }}>{brain.fresh ? 'current' : brain.builtAt ? 'stale' : 'not built'}</span><span className="cs-stat-delta u-muted">{brain.builtAt ? `built ${fmtDate(brain.builtAt)}` : 'builds on first question'}</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Chunks now</span><span className="cs-stat-value">{chunkCount}</span><span className="cs-stat-delta u-muted">{brain.indexedChunks} indexed</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Documents</span><span className="cs-stat-value">{docs.filter((d) => d.status === 'ready').length}<span className="u-muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 400 }}> / {docs.length}</span></span><span className="cs-stat-delta u-muted">ready / total</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Deal points</span><span className="cs-stat-value">{points.total}</span><span className="cs-stat-delta u-muted">{points.linked} linked · {points.suggested} suggested</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Gaps</span><span className="cs-stat-value" style={{ color: warn.length ? 'var(--warn)' : 'var(--ok)' }}>{warn.length}</span><span className="cs-stat-delta u-muted">{info.length} advisory</span></div>
      </div>

      {gaps.length > 0 && (
        <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <h4 style={{ margin: '0 0 var(--sp-2)' }}>Gaps</h4>
          {[...warn, ...info].map((g, i) => (
            <div key={i} className="kb-gap">
              <span className={`cs-badge ${sev(g.severity)}`} style={{ flexShrink: 0 }}>{g.code}</span>
              <span>{g.detail}{g.docId && <> <Link to={`/deals/${scope.deal}/docs/${g.docId}`}>open</Link></>}</span>
            </div>
          ))}
        </div>
      )}

      <section>
        <h4 style={{ margin: '0 0 var(--sp-2)' }}>What the brain holds, by source</h4>
        <div className="u-scroll-x">
          <table className="cs-table">
            <thead><tr><th>Source</th><th className="num">Chunks</th><th className="num">Indexed</th><th>What it carries</th></tr></thead>
            <tbody>
              {sourceRows.map(([s, n]) => (
                <tr key={s}><td><strong>{SOURCE_LABEL[s] || s}</strong></td><td className="num u-tabular">{n}</td><td className="num u-tabular u-muted">{brain.indexedSources?.[s] ?? 0}</td><td className="u-small u-muted">{SOURCE_HELP[s] || ''}</td></tr>
              ))}
              {Object.keys(SOURCE_LABEL).filter((s) => !bySource[s]).map((s) => (
                <tr key={s}><td className="u-muted">{SOURCE_LABEL[s]}</td><td className="num u-tabular u-muted">0</td><td className="num u-tabular u-muted">{brain.indexedSources?.[s] ?? 0}</td><td className="u-small u-muted">{SOURCE_HELP[s]}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h4 style={{ margin: '0 0 var(--sp-2)' }}>By document</h4>
        <div className="u-scroll-x">
          <table className="cs-table">
            <thead><tr><th>Document</th><th>Kind</th><th>Status</th><th className="num">Chunks</th><th>Role note</th><th>Tags</th></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td><Link to={`/deals/${scope.deal}/docs/${d.id}`}>{d.title}</Link>{d.versionCount ? <div className="u-xs u-muted">{d.versionCount} version{d.versionCount === 1 ? '' : 's'}</div> : null}</td>
                  <td className="u-small u-muted">{d.kind}{d.docType ? ` · ${d.docType}` : ''}</td>
                  <td><span className={`cs-badge ${docStatus(d.status)}`}>{d.status || 'unprocessed'}</span></td>
                  <td className="num u-tabular">{d.chunks}{d.chunks > 0 && <div className="u-xs u-muted">{Object.entries(d.sources).map(([s, n]) => `${n} ${s}`).join(' · ')}</div>}</td>
                  <td className="u-small">{d.objective ? <span className="u-clamp" style={{ '--lines': 2 }}>{d.objective}</span> : <span className="u-muted">—</span>}</td>
                  <td className="u-xs u-muted">{(d.tags || []).join(' · ') || '—'}</td>
                </tr>
              ))}
              {!docs.length && <tr><td colSpan={6} className="u-muted">No documents yet. Upload or draft one and the brain fills in.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="cs-card" style={{ padding: 'var(--sp-4)' }}>
        <h4 style={{ margin: '0 0 var(--sp-2)' }}>Project context</h4>
        <div className="u-small">
          <div><span className="u-muted">Structure note: </span>{deal.objective ? deal.objective : <span style={{ color: 'var(--warn)' }}>not set — the brain cannot say which document owns which layer</span>}</div>
          <div style={{ marginTop: 'var(--sp-1)' }}><span className="u-muted">Parties: </span>{deal.parties.length ? deal.parties.map((p) => p.label).join(', ') : <span className="u-muted">none</span>}</div>
          <div style={{ marginTop: 'var(--sp-1)' }}><span className="u-muted">Tags: </span>{deal.tags.length ? deal.tags.join(', ') : <span className="u-muted">none</span>}</div>
          <div style={{ marginTop: 'var(--sp-1)' }}><span className="u-muted">Deal points by status: </span>{Object.entries(points.byStatus).map(([s, n]) => `${n} ${s}`).join(' · ') || 'none'}</div>
        </div>
      </section>
    </div>
  );
}
